import React, { useState, useEffect, useCallback } from 'react';
import { Button, Box, useTheme, FormControl, InputLabel, Select, MenuItem, Typography, SelectChangeEvent, Skeleton } from '@mui/material';
import { useSnackbar } from 'notistack';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import { Grid } from "@material-ui/core";
import { validateBasicDateFilters } from '../../Utils/dateValidation';
import EstablishmentStorageUtils, { EstablishmentStorageData } from '../../Storages/establishmentStorage';
import { getToken as getAuthToken } from '../../Storages/cookies';

const primaryColor = '#1c2e4a';

interface EstablishmentItem {
    establishment_alias_id: number | string;
    establishment_id: number | string | null;
    description: string;
    cnpj?: string | null;
}



interface DrfPageFilterProps {
    onFiltersChange: (dataInicio?: string, dataFim?: string, establishmentId?: number | string | null) => void;
    onManualFetch?: () => void;
    initialDataInicio?: string;
    initialDataFim?: string;
    loading?: boolean;
}

const DrfPageFilter: React.FC<DrfPageFilterProps> = ({
    onFiltersChange,
    onManualFetch,
    initialDataInicio,
    initialDataFim,
    loading: externalLoading = false
}) => {
    const theme = useTheme();
    
    // Função para obter o primeiro dia do mês atual
    const getPrimeiroDiaDoMes = () => dayjs().startOf('month');
    
    // Função para obter o último dia do mês atual
    const getUltimoDiaDoMes = () => dayjs().endOf('month');
    
    const [dataInicio, setDataInicio] = useState<Dayjs | null>(
        initialDataInicio ? dayjs(initialDataInicio) : getPrimeiroDiaDoMes()
    );
    const [dataFim, setDataFim] = useState<Dayjs | null>(
        initialDataFim ? dayjs(initialDataFim) : getUltimoDiaDoMes()
    );
    
    // Estados para estabelecimento
    const [loadingEstablishments, setLoadingEstablishments] = useState(true);
    const [establishmentSelecionado, setEstablishmentSelecionado] = useState<number | string>('');
    const [establishments, setEstablishments] = useState<EstablishmentItem[]>([]);
    const [establishmentIdSelecionado, setEstablishmentIdSelecionado] = useState<number | string | null>(null);
    

    
    // Hook para notificações globais
    const { enqueueSnackbar } = useSnackbar();
    
    // Combina o loading interno (para validação) com o loading externo (da requisição)
    const isLoading = externalLoading;

    // Função para buscar estabelecimentos
    const fetchEstablishments = useCallback(async () => {
        try {
            const token = getAuthToken();
            const baseUrl = process.env.REACT_APP_API_URL || '';
            const url = baseUrl.endsWith('/') ? `${baseUrl}establishments-by-user` : `${baseUrl}/establishments-by-user`;

            const res = await fetch(url, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    Accept: 'application/json'
                }
            });
            const json = await res.json();

            if (Array.isArray(json?.data)) {
                // Ordena alfabeticamente pelo campo description
                const orderedData = [...json.data].sort((a: EstablishmentItem, b: EstablishmentItem) =>
                    a.description.localeCompare(b.description, 'pt-BR', { sensitivity: 'accent' })
                );

                setEstablishments(orderedData);

                if (json.data.length > 0) {
                    let estabelecimentoParaSelecionar: EstablishmentItem | null = null;

                    const salvo = EstablishmentStorageUtils.get();
                    if (salvo?.establishment_alias_id) {
                        const found = orderedData.find(
                            (e: EstablishmentItem) => e.establishment_alias_id == salvo.establishment_alias_id
                        );
                        if (found) {
                            estabelecimentoParaSelecionar = found;
                        }
                    }

                    if (!estabelecimentoParaSelecionar) {
                        estabelecimentoParaSelecionar = json.data[0];
                    }

                    if (estabelecimentoParaSelecionar) {
                        setEstablishmentSelecionado(estabelecimentoParaSelecionar.establishment_alias_id);
                        setEstablishmentIdSelecionado(estabelecimentoParaSelecionar.establishment_id);
                        EstablishmentStorageUtils.save({
                            establishment_alias_id: estabelecimentoParaSelecionar.establishment_alias_id,
                            establishment_id: estabelecimentoParaSelecionar.establishment_id,
                            description: estabelecimentoParaSelecionar.description,
                            cnpj: estabelecimentoParaSelecionar.cnpj || null
                        });
                    }
                }
            }
        } catch (error) {
            console.error('Erro ao buscar estabelecimentos', error);
        } finally {
            setLoadingEstablishments(false);
        }
    }, []);



    // Handlers para mudanças
    const handleEstablishmentChange = (event: SelectChangeEvent<string | number>) => {
        const selected = event.target.value;
        const estabelecimentoSelecionado = establishments.find(
            e => e.establishment_alias_id == selected
        );
        setEstablishmentSelecionado(selected);

        if (estabelecimentoSelecionado) {
            setEstablishmentIdSelecionado(estabelecimentoSelecionado.establishment_id);
            EstablishmentStorageUtils.save({
                establishment_alias_id: estabelecimentoSelecionado.establishment_alias_id,
                establishment_id: estabelecimentoSelecionado.establishment_id,
                description: estabelecimentoSelecionado.description,
                cnpj: estabelecimentoSelecionado.cnpj || null
            });
        }
    };



    // useEffect para carregar estabelecimentos na inicialização
    useEffect(() => {
        fetchEstablishments();
    }, [fetchEstablishments]);



    // Sincroniza as datas quando as props iniciais mudarem
    useEffect(() => {
        console.log({initialDataInicio, initialDataFim, establishmentIdSelecionado});
        if (initialDataInicio) {
            setDataInicio(dayjs(initialDataInicio));
        }
        if (initialDataFim) {
            setDataFim(dayjs(initialDataFim));
        }
    }, [initialDataInicio, initialDataFim]);

    useEffect(() => {
        const dataInicioFormatted = dataInicio?.format('YYYY-MM-DD');
        const dataFimFormatted = dataFim?.format('YYYY-MM-DD');

        const validation = validateBasicDateFilters(dataInicioFormatted, dataFimFormatted);


        if (validation.isValid) {
            onFiltersChange(dataInicioFormatted, dataFimFormatted, establishmentIdSelecionado);
        }
        
    }, [dataInicio, dataFim, establishmentIdSelecionado]);


    const handleFiltrar = () => {
        if (dataInicio && dataFim) {
            const dataInicioFormatted = dataInicio.format('YYYY-MM-DD');
            const dataFimFormatted = dataFim.format('YYYY-MM-DD');
            
            // Apenas valida as datas, não aplica os filtros
            const validation = validateBasicDateFilters(dataInicioFormatted, dataFimFormatted);
            
            if (!validation.isValid) {
                enqueueSnackbar(validation.error || 'Filtros de data inválidos', { variant: 'error' });
            }
        }

        if (dataInicio && dataFim && !isLoading) {
            const dataInicioFormatted = dataInicio.format('YYYY-MM-DD');
            const dataFimFormatted = dataFim.format('YYYY-MM-DD');
            
            // Valida as datas antes de aplicar os filtros
            const validation = validateBasicDateFilters(dataInicioFormatted, dataFimFormatted);
            
            if (validation.isValid) {
                // Atualiza os filtros com todos os parâmetros
                onFiltersChange(dataInicioFormatted, dataFimFormatted, establishmentIdSelecionado);
                
                // Faz a requisição manual se a função foi fornecida
                if (onManualFetch) {
                    onManualFetch();
                }
            }
        }
    };

    return (
        <>
            <Grid container spacing={2} >
                {/* Filtro de Estabelecimento */}
                <Grid item xs={12} sm={6} md={4} lg={4}>
                    {loadingEstablishments ? (
                        <Box display='flex' justifyContent='center' alignItems='center' gap={1} minHeight='40px'>
                            <Skeleton variant="circular" width={20} height={20} />
                            <Typography variant='body2' sx={{ color: '#666', fontSize: '0.8rem' }}>
                                Carregando...
                            </Typography>
                        </Box>
                    ) : establishments.length > 0 ? (
                        <FormControl size='small' fullWidth>
                            <InputLabel id='establishment-label'>Estabelecimento</InputLabel>
                            <Select
                                labelId='establishment-label'
                                value={establishmentSelecionado}
                                label='Estabelecimento'
                                onChange={handleEstablishmentChange}
                                disabled={isLoading}
                            >
                                {establishments.map(({ establishment_alias_id, description }) => (
                                    <MenuItem key={establishment_alias_id} value={establishment_alias_id}>
                                        {description}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    ) : null}
                </Grid>



                {/* Data Inicial */}
                <Grid item xs={12} sm={6} md={3} lg={3}>
                    <DatePicker
                        label="Data inicial"
                        value={dataInicio}
                        disabled={isLoading}
                        onChange={setDataInicio}
                        views={['year', 'month', 'day']}
                        slotProps={{
                            textField: {
                                fullWidth: true,
                                variant: 'outlined',
                                size: 'small'
                            }
                        }}
                    />
                </Grid>
                
                {/* Data Final */}
                <Grid item xs={12} sm={6} md={3} lg={3}>
                    <DatePicker
                        label="Data final"
                        value={dataFim}
                        disabled={isLoading}
                        onChange={setDataFim}
                        views={['year', 'month', 'day']}
                        slotProps={{
                            textField: {
                                fullWidth: true,
                                variant: 'outlined',
                                size: 'small'
                            }
                        }}
                    />
                </Grid>

                {/* Botão Filtrar */}
                <Grid item xs={12} sm={6} md={2} lg={2}>
                    <Button
                        variant="contained"
                        onClick={handleFiltrar}
                        disabled={isLoading || !dataInicio || !dataFim || !establishmentIdSelecionado}
                        sx={{
                            backgroundColor: primaryColor,
                            color: 'white',
                            '&:hover': {
                                backgroundColor: theme.palette.mode === 'dark' ? '#2c3e50' : '#34495e',
                                boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                            },
                            '&:disabled': {
                                backgroundColor: '#cccccc',
                                color: '#666666',
                            },
                            width: '100%',
                            minWidth: '120px',
                            height: '40px',
                            fontWeight: 600,
                            textTransform: 'none',
                            borderRadius: '4px',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}
                    >
                        {isLoading ? 'Carregando...' : 'Filtrar'}
                    </Button>
                </Grid>
            </Grid>

        </>
    );
};

export default DrfPageFilter;
